import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi, adminConfigApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'


export default function OrderDetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user, isSysAdmin } = useAuth()

    const [order, setOrder] = useState(null)
    const [config, setConfig] = useState(null)
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        load()
        loadConfig()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    async function load() {
        setLoading(true)
        try {
            const response = await orderApi.getById(id)
            if (response.success) setOrder(response.order)
            else toast.error(response.message || 'Could not load order')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function loadConfig() {
        try {
            const response = await adminConfigApi.get()
            if (response.success) setConfig(response.config)
        } catch { /* non-fatal */ }
    }

    async function handleCancel() {
        const reason = prompt(`Cancel order #${order.id}? (optional reason):`)
        if (reason === null) return
        setBusy(true)
        try {
            const response = await orderApi.cancel(order.id, reason)
            if (response.success) {
                toast.success('Order cancelled')
                load()
            } else {
                toast.error(response.message || 'Cancel failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handleStatusChange(newStatus) {
        const labels = {
            pending_payment: 'Pending Payment',
            paid: 'Paid',
            packed: 'Packed',
            picked_up: 'Picked Up',
        }
        if (!confirm(`Mark order #${order.id} as ${labels[newStatus] || newStatus}?`)) return
        setBusy(true)
        try {
            const response = await orderApi.updateStatus(order.id, { status: newStatus })
            if (response.success) {
                toast.success('Status updated')
                load()
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading order...</div>
    }

    if (!order) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Order not found</h2>
                <p className="text-gray-600 mt-2 text-sm">Order #{id} doesn't exist or you don't have access.</p>
                <div className="mt-4">
                    <Link to="/orders"><Button>Back to my orders</Button></Link>
                </div>
            </div>
        )
    }

    async function handlePreviewPdf() {
        try {
            const { blob } = await orderApi.fetchPdf(order.id, { inline: true })
            // Open the PDF in a new tab via a temporary blob URL
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            // Revoke after a delay so the new tab has time to load
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch (err) {
            toast.error('Could not load PDF')
        }
    }

    async function handleDownloadPdf() {
        try {
            const { blob, fileName } = await orderApi.fetchPdf(order.id, { inline: false })
            // Trigger download via a temporary anchor element
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (err) {
            toast.error('Could not download PDF')
        }
    }

    const canUserCancel = (order.status === 'pending_payment' || order.status === 'paid')
        && order.submittedByUserId === user?.id
    const totalFilled = order.items.reduce((s, i) => s + i.filledQuantity, 0)
    const totalBackordered = order.items.reduce((s, i) => s + i.backorderQuantity, 0)
    const litEmail = config?.literatureEmail
    const isCancelled = order.status === 'cancelled'

    return (
        <div className="space-y-4 max-w-4xl mx-auto">
            {/* ----- Header ----------------------------------------------------- */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">
                        Order #{order.id}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {order.customerName} · Submitted by {order.submitterName} ·{' '}
                        {new Date(order.createdAt).toLocaleString('en-US', {
                            month: 'long', day: 'numeric', year: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                        })}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="secondary" onClick={handlePreviewPdf} disabled={busy}>
                        Preview PDF
                    </Button>
                    <Button variant="secondary" onClick={handleDownloadPdf} disabled={busy}>
                        Download PDF
                    </Button>
                    <Link to={isSysAdmin ? '/admin/orders' : '/orders'}>
                        <Button variant="secondary">Back</Button>
                    </Link>
                </div>
            </div>

            {/* ----- Status + cycle + totals card ------------------------------ */}
            <div className="bg-white rounded-xl shadow-sm p-6 grid sm:grid-cols-3 gap-4 text-sm">
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Status</div>
                    <StatusBadge status={order.status} />
                </div>
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Cycle</div>
                    <div className="font-medium">{cycleToDisplay(order.cycleLabel)}</div>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Total</div>
                    <div className="font-semibold text-lg text-ghasc-navy">
                        ${Number(order.subtotal).toFixed(2)}
                    </div>
                </div>
            </div>

            {/* ----- Cancellation banner (if cancelled) ------------------------ */}
            {isCancelled && (
                <div className="bg-gray-100 border border-gray-300 rounded-xl p-4 text-sm text-gray-700">
                    <div className="font-semibold mb-1">This order was cancelled</div>
                    {order.cancelledAt && (
                        <p>Cancelled on {new Date(order.cancelledAt).toLocaleString()}</p>
                    )}
                    {order.cancellationReason && (
                        <p className="mt-1 italic">"{order.cancellationReason}"</p>
                    )}
                </div>
            )}

            {/* ----- Payment instructions (only when payment pending) ---------- */}
            {order.status === 'pending_payment' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Payment</h3>
                    <div className="bg-amber-50 border-l-4 border-ghasc-tan p-4 rounded-r">
                        <p className="text-gray-800">
                            Send <strong>${Number(order.subtotal).toFixed(2)}</strong> by e-transfer to:
                        </p>
                        {litEmail ? (
                            <p className="font-mono text-lg text-ghasc-navy mt-2">{litEmail}</p>
                        ) : (
                            <p className="text-amber-900 mt-2 text-sm">
                                (Literature email isn't set in Admin Config.)
                            </p>
                        )}
                        <p className="text-sm text-gray-700 mt-2">
                            Include <strong>Order #{order.id}</strong> in the e-transfer message.
                        </p>
                    </div>
                </div>
            )}

            {/* ----- Backorder note ------------------------------------------- */}
            {order.backorderCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                    <div className="font-semibold mb-1">⚠ Some items are backordered</div>
                    <p>
                        {totalFilled} item{totalFilled === 1 ? '' : 's'} will be ready for the next pickup.{' '}
                        {totalBackordered} item{totalBackordered === 1 ? '' : 's'} are backordered and will be filled
                        from the next regional shipment.
                    </p>
                </div>
            )}

            {/* ----- Items ----------------------------------------------------- */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-ghasc-navy">Items</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <tr>
                                <th className="px-4 py-2">Item</th>
                                <th className="px-4 py-2 text-right w-20">Cost</th>
                                <th className="px-4 py-2 text-right w-16">Qty</th>
                                <th className="px-4 py-2 text-right w-20">Filled</th>
                                <th className="px-4 py-2 text-right w-24">Backorder</th>
                                <th className="px-4 py-2 text-right w-24">Line</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {order.items.map(line => {
                                const lineTotal = Number(line.cost) * Number(line.requestedQuantity)
                                const variantLabel = [line.year, line.color].filter(Boolean).join(' / ')
                                return (
                                    <tr key={line.id}>
                                        <td className="px-4 py-2">
                                            <div className="text-sm text-gray-900">{line.description}</div>
                                            <div className="text-xs text-gray-500 font-mono">
                                                {line.itemNumber}
                                                {variantLabel && <span className="ml-2 font-sans">— {variantLabel}</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-right text-sm">${Number(line.cost).toFixed(2)}</td>
                                        <td className="px-4 py-2 text-right text-sm">{line.requestedQuantity}</td>
                                        <td className="px-4 py-2 text-right text-sm">{line.filledQuantity}</td>
                                        <td className="px-4 py-2 text-right text-sm">
                                            {line.backorderQuantity > 0
                                                ? <span className="text-amber-700">{line.backorderQuantity}</span>
                                                : '—'}
                                        </td>
                                        <td className="px-4 py-2 text-right text-sm font-medium">${lineTotal.toFixed(2)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                            <tr>
                                <td colSpan="5" className="px-4 py-3 text-right text-sm font-medium text-gray-700">Total</td>
                                <td className="px-4 py-3 text-right text-lg font-semibold text-ghasc-navy">
                                    ${Number(order.subtotal).toFixed(2)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* ----- Status timeline ------------------------------------------ */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Timeline</h3>
                <ul className="text-sm space-y-1 text-gray-700">
                    <li><strong>Submitted:</strong> {new Date(order.createdAt).toLocaleString()}</li>
                    {order.paidAt && (
                        <li>
                            <strong>Paid:</strong> {new Date(order.paidAt).toLocaleString()}
                            {order.paymentMethod && (
                                <span className="text-gray-500"> via {paymentMethodLabel(order.paymentMethod)}</span>
                            )}
                            {order.paymentNotes && (
                                <div className="ml-4 text-xs italic text-gray-500">"{order.paymentNotes}"</div>
                            )}
                        </li>
                    )}
                    {order.packedAt && <li><strong>Packed:</strong> {new Date(order.packedAt).toLocaleString()}</li>}
                    {order.pickedUpAt && <li><strong>Picked up:</strong> {new Date(order.pickedUpAt).toLocaleString()}</li>}
                    {order.cancelledAt && <li><strong>Cancelled:</strong> {new Date(order.cancelledAt).toLocaleString()}</li>}
                </ul>
            </div>

            {/* ----- User actions: cancel ------------------------------------- */}
            {canUserCancel && !isSysAdmin && (
                <div className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-between flex-wrap gap-3">
                    <p className="text-sm text-gray-700">
                        You can still cancel this order — the chair hasn't started packing it yet.
                    </p>
                    <Button variant="danger" onClick={handleCancel} loading={busy}>
                        Cancel Order
                    </Button>
                </div>
            )}

            {/* ----- Admin actions: status transitions ------------------------ */}
            {isSysAdmin && !isCancelled && (
                <div className="bg-white rounded-xl shadow-sm p-6 space-y-3">
                    <h3 className="text-lg font-semibold text-ghasc-navy">Admin actions</h3>
                    <div className="flex flex-wrap gap-2">
                        {order.status === 'pending_payment' && (
                            <Button onClick={() => handleStatusChange('paid')} loading={busy}>
                                Mark as Paid
                            </Button>
                        )}
                        {(order.status === 'pending_payment' || order.status === 'paid') && (
                            <Button onClick={() => handleStatusChange('packed')} loading={busy}>
                                Mark as Packed
                            </Button>
                        )}
                        {order.status === 'packed' && (
                            <Button onClick={() => handleStatusChange('picked_up')} loading={busy}>
                                Mark as Picked Up
                            </Button>
                        )}
                        <Button variant="danger" onClick={handleCancel} loading={busy}>
                            Cancel Order
                        </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                        Cancelling restores inventory for any filled quantities.
                    </p>
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
    const map = {
        pending_payment: { label: 'Pending Payment', cls: 'bg-amber-100 text-amber-800' },
        paid: { label: 'Paid', cls: 'bg-blue-100 text-blue-800' },
        packed: { label: 'Packed', cls: 'bg-indigo-100 text-indigo-800' },
        picked_up: { label: 'Picked Up', cls: 'bg-green-100 text-green-800' },
        cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600' },
    }
    const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' }
    return (
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${m.cls}`}>
            {m.label}
        </span>
    )
}

function paymentMethodLabel(method) {
    if (method === 'etransfer') return 'E-transfer'
    if (method === 'cash') return 'Cash'
    return method || '—'
}

function cycleToDisplay(label) {
    if (!label || label.length !== 7) return label || ''
    const year = parseInt(label.slice(0, 4), 10)
    const month = parseInt(label.slice(5), 10)
    if (isNaN(year) || isNaN(month)) return label
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[month - 1]} ${year}`
}